import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, Clock, Info, HourglassIcon, Loader2 } from 'lucide-react';
import {
  fetchMyDownloads, friendlyError, formatMoney, generateDownloadLink,
  generateAssetDownloadLink, buildDownloadLinkUrl,
} from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';
import { EmptyState } from '../EmptyState';
import { STATUS_BADGE_COLORS, STATUS_LABEL } from '../AccessBadge';

export function MyDownloadsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await fetchMyDownloads();
      setItems(data.items || []);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const onGenerate = async (item) => {
    if (busyId) return;
    setBusyId(item.entitlement_id);
    try {
      // Fase 06D · si el item ya está listo (download_asset_id + ready_to_download)
      // usamos el nuevo flujo con enlaces firmados por el proveedor.
      if (item.ready_to_download && item.download_asset_id) {
        const { data } = await generateAssetDownloadLink(item.download_asset_id);
        const url = buildDownloadLinkUrl(data.token);
        window.location.href = url;
        return;
      }
      // Fallback legacy (Fase pre-06B). Sigue devolviendo MOCK hasta que
      // exista un asset activo asociado.
      const res = await generateDownloadLink(item.entitlement_id);
      if (res?.download_available === false || res?.is_mock) {
        toast.info('La descarga real estará disponible cuando el club prepare el archivo.');
      } else if (res?.download_url) {
        window.open(res.download_url, '_blank', 'noopener');
      }
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
      <SectionTitle
        icon={Download}
        label="Mis descargas"
        hint="Tus accesos de descarga adquiridos."
      />
      {loading ? (
        <div className="space-y-2">{[1, 2].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Download}
          title="Aún no tienes descargas"
          description="Cuando compres una descarga aparecerá aquí."
          ctaLabel="Ver partidos disponibles"
          ctaTo="/eventos"
          testId={DASHBOARD.downloadsEmpty}
        />
      ) : (
        <>
          <ul className="space-y-3">
            {items.map((d) => {
              const ready = !!d.ready_to_download && !!d.download_asset_id;
              const isBusy = busyId === d.entitlement_id;
              return (
                <li
                  key={d.entitlement_id}
                  data-testid={DASHBOARD.downloadRow(d.entitlement_id)}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-4 rounded-xl border border-[var(--ns-border)]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{d.title}</p>
                      <Badge className={`text-[10px] uppercase ${STATUS_BADGE_COLORS[d.status] || ''}`}>
                        {STATUS_LABEL[d.status] || d.status}
                      </Badge>
                      {/* El badge genérico de arriba ya muestra la etiqueta
                          correcta para `exhausted` ("Límite alcanzado"),
                          `expired` ("Caducada") y `revoked` ("Revocada")
                          vía STATUS_LABEL. Aquí sólo añadimos la indicación
                          auxiliar que NO existe en el status genérico:
                          "Archivo en preparación" para active+!ready. */}
                      {d.status === 'active' && !ready ? (
                        <Badge className="text-[10px] uppercase bg-amber-100 text-amber-800">
                          <HourglassIcon className="h-3 w-3 mr-1" />
                          Archivo en preparación
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-[var(--ns-text-muted)] mt-1 flex items-center gap-1 flex-wrap">
                      {d.file_name ? (
                        <span className="font-mono text-[10px]">{d.file_name}</span>
                      ) : null}
                      {d.ends_at ? (
                        <>
                          <span className="mx-1">·</span>
                          <Clock className="h-3 w-3" />
                          Caduca {new Date(d.ends_at).toLocaleDateString('es-ES')}
                        </>
                      ) : null}
                      {(d.max_downloads || d.downloads_max) ? (
                        <>
                          <span className="mx-1">·</span>
                          {d.downloads_used || 0}/{d.max_downloads || d.downloads_max} descargas
                        </>
                      ) : null}
                    </p>
                  </div>
                  {ready ? (
                    <Button
                      size="sm"
                      disabled={isBusy}
                      onClick={() => onGenerate(d)}
                      data-testid={DASHBOARD.downloadBtn(d.entitlement_id)}
                      className="h-9 px-3 rounded-lg bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0 gap-1"
                    >
                      {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      Descargar vídeo
                    </Button>
                  ) : d.status === 'active' ? (
                    <Button
                      size="sm"
                      disabled
                      data-testid={DASHBOARD.downloadBtn(d.entitlement_id)}
                      className="h-9 px-3 rounded-lg bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border border-[var(--ns-border)] cursor-not-allowed gap-1"
                      title="El archivo aún no está preparado."
                    >
                      <HourglassIcon className="h-3.5 w-3.5" />
                      Descarga próximamente
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <p className="mt-4 text-[11px] text-[var(--ns-text-subtle)] flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            Los enlaces de descarga son temporales y se generan bajo demanda desde el proveedor externo tras validar tu acceso. Nunca se comparten con terceros.
          </p>
        </>
      )}
    </Card>
  );
}
